/**
 * @file
 * Limits product teaser metadata to four visible text lines.
 *
 * Applies the editorial title ceiling before sharing the remaining lines.
 * CSS clamps are disabled during measurement so overflow cannot go unnoticed:
 *
 * - at most four visible text lines inside `.teaser-card__meta`
 * - existing typography remains unchanged
 * - responsive widths are recalculated automatically
 * - truncated text always ends with an ellipsis
 */

(function (Drupal, once) {
  "use strict";

  const instances = new WeakMap();
  const SELECTOR = ".teaser-card--product .teaser-card__meta";
  const MAX_LINES = 4;
  const ELLIPSIS = "…";
  const LINE_TOLERANCE = 1;
  // One observer for all cards, with enough lead-in for scrolling and shuffle.
  const visibilityObserver = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        instances.get(target)?.setNearViewport(isIntersecting);
      });
    }, { rootMargin: '600px 0px' })
    : null;

  /**
   * Returns all visible text nodes inside an element.
   *
   * Visually hidden accessibility labels are intentionally excluded from
   * line measurements, but their DOM content is left untouched.
   */
  function getTextNodes(element) {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          if (!node.textContent.trim()) {
            return NodeFilter.FILTER_REJECT;
          }

          const parent = node.parentElement;

          if (!parent) {
            return NodeFilter.FILTER_REJECT;
          }

          if (parent.closest(".visually-hidden")) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        },
      },
    );

    const nodes = [];
    let node = walker.nextNode();

    while (node) {
      nodes.push(node);
      node = walker.nextNode();
    }

    return nodes;
  }

  /** Measure all text, including overflow: clipping must not hide the problem. */
  function getNodeRects(node) {
    const range = document.createRange();
    range.selectNodeContents(node);
    return Array.from(range.getClientRects()).filter(
      (rect) => rect.width > 0 && rect.height > 0,
    );
  }

  /**
   * Groups text fragments that share approximately the same vertical
   * position into visual lines.
   */
  function getTextLines(container) {
    const fragments = [];

    getTextNodes(container).forEach((node) => {
      getNodeRects(node).forEach((rect) => {
        fragments.push({
          node,
          rect,
        });
      });
    });

    fragments.sort((a, b) => {
      const verticalDifference = a.rect.top - b.rect.top;

      if (Math.abs(verticalDifference) > LINE_TOLERANCE) {
        return verticalDifference;
      }

      return a.rect.left - b.rect.left;
    });

    const lines = [];

    fragments.forEach((fragment) => {
      let line = lines.find(
        (candidate) =>
          Math.abs(candidate.top - fragment.rect.top) <= LINE_TOLERANCE,
      );

      if (!line) {
        line = {
          top: fragment.rect.top,
          bottom: fragment.rect.bottom,
          fragments: [],
        };

        lines.push(line);
      }

      line.bottom = Math.max(line.bottom, fragment.rect.bottom);
      line.fragments.push(fragment);
    });

    lines.sort((a, b) => a.top - b.top);

    return lines;
  }

  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

  /** Hide separators whose following visual text has been removed. */
  function updateBreaks(state) {
    state.breaks.forEach(({ element }) => {
      element.hidden = !state.textNodes.some(({ node }) =>
        node.textContent.trim() && element.parentElement.contains(node)
        && (element.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING),
      );
    });
  }

  /**
   * Shorten a prefix across text nodes without splitting grapheme clusters.
   * A single ellipsis replaces everything omitted, including later fields.
   */
  function limitText(entries, state, fits) {
    if (fits()) return;
    const parts = entries.map(({ node }) => ({
      node,
      characters: Array.from(segmenter.segment(node.textContent.trim()), ({ segment }) => segment),
    }));
    const length = parts.reduce((sum, part) => sum + part.characters.length, 0);
    if (!length) return;

    function apply(length) {
      let remaining = length;
      let last = parts[0].node;
      parts.forEach(({ node, characters }) => {
        const count = Math.min(remaining, characters.length);
        node.textContent = characters.slice(0, count).join('');
        if (count) last = node;
        remaining -= count;
      });
      last.textContent = last.textContent.trimEnd().replace(/…$/, '') + ELLIPSIS;
      updateBreaks(state);
    }

    let low = 0;
    let high = Math.max(0, length - 1);
    let best = 0;
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      apply(middle);
      if (fits()) {
        best = middle;
        low = middle + 1;
      }
      else {
        high = middle - 1;
      }
    }
    apply(best);
  }

  /** Apply field ceilings first, then share the actual remaining space. */
  function clamp(container, state) {
    state.textNodes.forEach(({ node, text }) => { node.textContent = text; });
    state.breaks.forEach(({ element, hidden }) => { element.hidden = hidden; });
    const title = container.querySelector('.teaser-card__title');
    if (!title) return;
    const styles = getComputedStyle(container);
    const titleLimit = Number(styles.getPropertyValue('--title-lines')) || MAX_LINES;
    limitText(state.textNodes.filter(({ node }) => title.contains(node)), state,
      () => getTextLines(title).length <= Math.min(MAX_LINES, titleLimit));

    const subtitle = container.querySelector('.teaser-card__subtitle');
    const subtitleLimit = Number(styles.getPropertyValue('--subtitle-lines')) || MAX_LINES;
    if (subtitle) {
      limitText(state.textNodes.filter(({ node }) => subtitle.contains(node)), state,
        () => getTextLines(subtitle).length <= Math.min(MAX_LINES, subtitleLimit));
    }

    const availableHeight = container.clientHeight
      - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
    limitText(state.textNodes, state, () => {
      const height = Array.from(container.children).reduce((sum, child) => {
        const style = getComputedStyle(child);
        return sum + child.getBoundingClientRect().height
          + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
      }, 0);
      return getTextLines(container).length <= MAX_LINES
        && getTextLines(title).length <= Math.min(MAX_LINES, titleLimit)
        && (!subtitle || getTextLines(subtitle).length <= Math.min(MAX_LINES, subtitleLimit))
        && height <= availableHeight + 0.5;
    });
  }

  /**
   * Prepare visual copies only when a card first approaches the viewport.
   */
  function prepareText(container) {
    // Keep original text in the semantic heading/paragraph. Only the
    // aria-hidden visual copy is measured and shortened. Existing hidden
    // role labels (Authors/Editors) remain in their original position.
    return getTextNodes(container).map((original) => {
      const full = document.createElement('span');
      full.className = 'visually-hidden';
      full.dataset.teaserFullText = '';
      const visual = document.createElement('span');
      visual.setAttribute('aria-hidden', 'true');
      visual.dataset.teaserVisualText = '';
      const node = original.cloneNode();
      original.before(full, visual);
      full.appendChild(original);
      visual.appendChild(node);
      return { original, full, visual, node, text: original.textContent };
    });
  }

  /**
   * Measure only nearby cards; CSS remains the offscreen/no-JS fallback.
   */
  function install(container) {
    let state;
    let disposed = false;
    let nearViewport = !visibilityObserver;
    let frame;

    let scheduled = false;
    let previousLayout = null;

    function update() {
      scheduled = false;

      if (disposed || !nearViewport || !container.isConnected) {
        return;
      }

      const width = container.getBoundingClientRect().width;

      if (width <= 0) {
        return;
      }

      // Height, typography and the editorial cap can change at the same width.
      const typography = Array.from(container.querySelectorAll('h2, p')).map((element) => {
        const style = getComputedStyle(element);
        return [style.fontFamily, style.fontSize, style.fontWeight, style.lineHeight, style.letterSpacing].join(':');
      }).join('|');
      const styles = getComputedStyle(container);
      const layout = [width, container.clientHeight, typography, styles.padding,
        styles.getPropertyValue('--title-lines'), styles.getPropertyValue('--subtitle-lines')].join('|');
      if (previousLayout === layout) return;
      previousLayout = layout;

      if (!state) {
        state = {
          textNodes: prepareText(container),
          breaks: Array.from(container.querySelectorAll('br')).map((element) => ({ element, hidden: element.hidden })),
        };
        container.classList.add('teaser-card__meta--measured');
      }
      clamp(container, state);
    }

    function scheduleUpdate() {
      if (disposed || !nearViewport || scheduled) {
        return;
      }

      scheduled = true;
      frame = requestAnimationFrame(update);
    }

    const resizeObserver = new ResizeObserver(scheduleUpdate);

    function observeSize() {
      resizeObserver.observe(container);
      // Text-only zoom and typography changes need not resize the fixed card.
      const text = container.querySelector('.teaser-card__text');
      if (text) resizeObserver.observe(text);
    }

    function setNearViewport(near) {
      if (disposed || nearViewport === near) return;
      nearViewport = near;
      if (near) {
        observeSize();
        scheduleUpdate();
      }
      else {
        resizeObserver.disconnect();
        cancelAnimationFrame(frame);
        scheduled = false;
      }
    }

    /*
     * Wait for web fonts before performing the final measurement because
     * font metrics directly affect line wrapping.
     */
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        previousLayout = null;
        scheduleUpdate();
      });
    }

    window.addEventListener('resize', scheduleUpdate);
    document.fonts?.addEventListener('loadingdone', invalidate);
    function invalidate() {
      previousLayout = null;
      scheduleUpdate();
    }

    instances.set(container, {
      setNearViewport,
      dispose() {
        disposed = true;
        cancelAnimationFrame(frame);
        resizeObserver.disconnect();
        visibilityObserver?.unobserve(container);
        window.removeEventListener('resize', scheduleUpdate);
        document.fonts?.removeEventListener('loadingdone', invalidate);
        container.classList.remove('teaser-card__meta--measured');
        state?.breaks.forEach(({ element, hidden }) => { element.hidden = hidden; });
        state?.textNodes.forEach(({ original, full, visual }) => {
          full.replaceWith(original);
          visual.remove();
        });
      },
    });
    if (visibilityObserver) {
      visibilityObserver.observe(container);
    }
    else {
      observeSize();
      scheduleUpdate();
    }
  }

  Drupal.behaviors.productTeaserClamp = {
    attach(context) {
      once(
        "product-teaser-clamp",
        SELECTOR,
        context,
      ).forEach((container) => {
        install(container);
      });
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      const elements = Array.from(context.querySelectorAll(SELECTOR));
      if (context.matches?.(SELECTOR)) elements.push(context);
      // Wait for AJAX removal; an unmatched replacement may detach document.
      queueMicrotask(() => elements.forEach((container) => {
        if (container.isConnected) return;
        instances.get(container)?.dispose();
        instances.delete(container);
        once.remove('product-teaser-clamp', container);
      }));
    },
  };
})(Drupal, once);
